import React from 'react';
import PropTypes from 'prop-types';
import { Col } from 'react-flexa';
import { withStyles } from '@material-ui/core/styles';
import Typography from '../../../../components/Typography';
import Image from '../../../../components/Image';
import BuyButton from './BuyButton';
import CheckoutModal from './CheckoutModal';
import startCase from 'lodash/startCase';
import ProcessPaymentModal from '../SignUp/ProcessPaymentModal';
import NewMembershipLoginModal from '../SignUp/NewMembershipLoginModal';
import { getPenguinsData } from '../../../../helpers';
import { integrateMember, sendDataToListrak } from '../../../api/membership';
import { PAYMENT_METHOD_CREDIT_CARD } from '../SignUp/helpers';
import NewMembershipErrorModal from '../SignUp/NewMembershipErrorModal';

const styles = theme => ({
  pricingPlanBuy: {
    position: 'relative',
    backgroundColor: '#FFFFFF',
    border: '3px solid #FFFFFF',
    boxShadow: '0 20px 20px 0 rgba(0,0,0,0.1)',
    borderRadius: '2px',
    margin: '0 8px',
    padding: '3rem 3rem',
    [theme.breakpoints.down('sm')]: {
      margin: '20px auto'
    },
    [theme.breakpoints.down('375')]: {
      padding: '3rem 1rem'
    },
    '&:hover': {
      borderColor: theme.palette.primary.main,
      transition: 'all 1s',
      webkitTransition: 'all 1s'
    }
  },
  buyHead: {
    color: 'white',
    backgroundColor: theme.palette.secondary.dark,
    maxWidth: '50%',
    margin: '8px auto',
    padding: '0.5rem 1rem',
    borderRadius: '2px'
  },
  monthlyPrice: {
    fontSize: '4rem',
    padding: '1rem 1rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2.5rem'
    }
  },
  additionalText: {
    marginTop: '10px',
    marginBottom: '10px',
    letterSpacing: '1px'
  },
  horizontalDivider: {
    maxWidth: '75%',
    border: '1px solid #CDD3D7'
  },
  savings: {
    backgroundColor: theme.palette.primary.main,
    color: '#efefef',
    padding: 20,
    position: 'absolute',
    borderRadius: '50%',
    top: '-5%',
    right: '-2%',
    maxWidth: '30%'
  }
});

class PricingPlanBuy extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showCheckoutModal: false,
      showErrorModal: false,
      selectedPaymentMethod: null, // set by CheckoutModal on close
      membershipLogin: {
        password: '',
        success: false,
        username: ''
      },
      error: ''
    };
    this.sessionKey = getPenguinsData(['view_data', 'session_key']);
  }

  handleOnClick = () => {
    this.setState({ showCheckoutModal: true });
  };

  handleModalClose = () => {
    this.setState({ showCheckoutModal: false });
  };

  onSignUpSuccess = selectedPaymentMethod => {
    const paymentMethodStr =
      selectedPaymentMethod === PAYMENT_METHOD_CREDIT_CARD ? 'cc' : 'ach';
    this.setState({ selectedPaymentMethod: paymentMethodStr });
  };

  setFailureState = () => {
    this.setState({ showErrorModal: true });
  };

  onCapturePaymentAmount = () => {
    integrateMember(this.sessionKey)
      .then(({ data }) => {
        if (data.success) {
          if(data.data)
            sendDataToListrak(data.data, data.username);
          this.setState({
            membershipLogin: {
              username: data.username,
              password: data.password,
              success: true
            },
            selectedPaymentMethod: null
          });
        } else {
          this.setFailureState();
        }
      })
      .catch(this.setFailureState);
  };

  render() {
    const {
      classes,
      type,
      typeId,
      trackingId,
      monthlyPrice,
      installmentPrice,
      initiationPrice,
      savings
    } = this.props;
    const { showCheckoutModal } = this.state;
    return (
      <Col
        gutter={2}
        xs={12}
        sm={12}
        md={12}
        lg={4}
        className={classes.pricingPlanBuy}
      >
        {savings && (
          <div className={classes.savings}>
            <b>
              SAVE
              <br />
              {savings}%
            </b>
          </div>
        )}
        <Typography upper={true} bold={true} className={classes.buyHead}>
          {type}
        </Typography>
        <div style={{ margin: '0 auto' }}>
          <Typography color="secondary.dark" className={classes.monthlyPrice}>
            ${monthlyPrice}
            <Image src="/pricingpage/per-month.png" style={{ width: '15%' }} />
          </Typography>
        </div>
        <hr className={classes.horizontalDivider} />
        <Typography
          variant="body2"
          color="secondary.dark"
          bold={true}
          className={classes.additionalText}
        >
          + ${initiationPrice} Initiation (One-Time only)
        </Typography>
        <Typography
          variant="body2"
          color="secondary.dark"
          bold={true}
          className={classes.additionalText}
        >
          *Recurring {startCase(type)} Payment
        </Typography>
        <Typography
          variant="body2"
          color="secondary.muted"
          bold={true}
          className={classes.additionalText}
        >
          (CANCEL ANYTIME)
        </Typography>
        <BuyButton onClick={this.handleOnClick} />
        <Typography bold={true} variant="subtitle2" color="secondary.dark">
          30 Day Money-Back Guarantee
        </Typography>
        <CheckoutModal
          isOpen={showCheckoutModal}
          trackingId={trackingId}
          type={type}
          typeId={typeId}
          installmentPrice={installmentPrice}
          initiationPrice={initiationPrice}
          handleClose={this.handleModalClose}
          onSignUpSuccess={this.onSignUpSuccess}
        />
        {this.state.selectedPaymentMethod && (
          <ProcessPaymentModal
            sessionKey={this.sessionKey}
            paymentMethod={this.state.selectedPaymentMethod}
            onSuccess={this.onCapturePaymentAmount}
          />
        )}
        {this.state.membershipLogin.success && (
          <NewMembershipLoginModal
            username={this.state.membershipLogin.username}
            password={this.state.membershipLogin.password}
          />
        )}
        {this.state.showErrorModal && <NewMembershipErrorModal />}
      </Col>
    );
  }
}

PricingPlanBuy.defaultProps = {
  trackingId: '531393'
};

PricingPlanBuy.propTypes = {
  classes: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  typeId: PropTypes.string.isRequired,
  trackingId: PropTypes.string,
  monthlyPrice: PropTypes.string.isRequired,
  installmentPrice: PropTypes.string.isRequired,
  initiationPrice: PropTypes.string.isRequired,
  savings: PropTypes.string
};

export default withStyles(styles)(PricingPlanBuy);
